import type { Feature, TariffType } from '@/shared/config/permissions'
import { computed, ref } from 'vue'
import { useAuthStore } from '@/entities/auth'
import {
  PREMIUM_ONLY_FEATURES,
  TARIFF_FEATURES,
  TARIFF_TYPES,
} from '@/shared/config/permissions'

// Global state for upgrade modal
const showUpgradeModal = ref(false)
const upgradeFeature = ref<Feature | null>(null)

export function usePermissions() {
  const authStore = useAuthStore()

  // Get the current tariff type from profile
  const currentTariff = computed<TariffType | null>(() => {
    const tariff = authStore.profile?.current_tariff?.tariff
    if (!tariff) 
      return null

    const tariffType = tariff.type?.toLowerCase()
    if (tariffType === TARIFF_TYPES.PREMIUM) {
      return TARIFF_TYPES.PREMIUM
    }
    if (tariffType === TARIFF_TYPES.ESSENTIAL) {
      return TARIFF_TYPES.ESSENTIAL
    }

    // Check by name as fallback
    const tariffName = tariff.name?.toLowerCase()
    if (tariffName?.includes('premium')) {
      return TARIFF_TYPES.PREMIUM
    }
    if (tariffName?.includes('essential')) {
      return TARIFF_TYPES.ESSENTIAL
    }

    return null
  })

  // Boolean helpers
  const isPremium = computed(() => currentTariff.value === TARIFF_TYPES.PREMIUM)
  const isEssential = computed(
    () => currentTariff.value === TARIFF_TYPES.ESSENTIAL,
  )
  const hasSubscription = computed(() => currentTariff.value !== null)

  // Check if user has access to a specific feature
  const hasAccess = (feature: Feature): boolean => {
    const tariff = currentTariff.value

    // No subscription = no access
    if (!tariff) 
      return false

    const allowedFeatures = TARIFF_FEATURES[tariff]
    return allowedFeatures?.includes(feature) ?? false
  }

  // Reactive computed version for templates
  const canAccess = (feature: Feature) => {
    return computed(() => hasAccess(feature))
  }

  // Check if feature requires upgrade
  const requiresUpgrade = (feature: Feature): boolean => {
    return PREMIUM_ONLY_FEATURES.includes(feature) && !isPremium.value
  }

  // Execute callback if user has access, otherwise show upgrade modal
  const gateFeature = (feature: Feature, callback: () => void) => {
    if (hasAccess(feature)) {
      callback()
    }
    else {
      openUpgradeModal(feature)
    }
  }

  // Modal control
  const openUpgradeModal = (feature?: Feature) => {
    upgradeFeature.value = feature ?? null
    showUpgradeModal.value = true
  }

  const closeUpgradeModal = () => {
    showUpgradeModal.value = false
    upgradeFeature.value = null
  }

  return {
    // Tariff state
    currentTariff,
    isPremium,
    isEssential,
    hasSubscription,

    // Access checks
    hasAccess,
    canAccess,
    requiresUpgrade,
    gateFeature,

    // Modal control
    showUpgradeModal,
    upgradeFeature,
    openUpgradeModal,
    closeUpgradeModal,
  }
}
